/**
 * useSubscriptions.ts
 *
 * GET/POST/DELETE /subscriptions. A successful subscribe/unsubscribe
 * invalidates both the subscriptions list and any active project-search
 * queries (key prefix ["projects", "search"]) so the picker's star state
 * and "Subscribed" grouping stay in sync without manual refetch calls.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api } from "../api/client";
import type {
  ApiError,
  Subscription,
  SubscriptionListResponse,
} from "../api/types";

const SUBSCRIPTIONS_KEY = ["subscriptions"] as const;
const PROJECTS_SEARCH_KEY = ["projects", "search"] as const;

export function useSubscriptions(): UseQueryResult<
  SubscriptionListResponse,
  ApiError
> {
  return useQuery<SubscriptionListResponse, ApiError>({
    queryKey: SUBSCRIPTIONS_KEY,
    queryFn: api.listSubscriptions,
  });
}

export function useSubscribeProject(): UseMutationResult<
  Subscription,
  ApiError,
  number
> {
  const queryClient = useQueryClient();
  return useMutation<Subscription, ApiError, number>({
    mutationFn: (projectId) => api.subscribe(projectId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SUBSCRIPTIONS_KEY });
      void queryClient.invalidateQueries({ queryKey: PROJECTS_SEARCH_KEY });
    },
  });
}

export function useUnsubscribeProject(): UseMutationResult<
  void,
  ApiError,
  number
> {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: (projectId) => api.unsubscribe(projectId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SUBSCRIPTIONS_KEY });
      void queryClient.invalidateQueries({ queryKey: PROJECTS_SEARCH_KEY });
    },
  });
}
